import { useCallback } from "react";

import { type Tag } from "@/features/player-kanban/types/types";

import RemovablePill from "@/components/molecules/RemovablePill";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/**
 * Props for TagSelectorCurrentTags component
 */
interface TagSelectorCurrentTagsProps {
  currentPlayerTags: (Tag & { color: string })[];
  onToggleTag: (tag: Tag) => void;
}

/**
 * TagSelectorCurrentTags View Component
 * Pure UI component for displaying current player tags with removal capability.
 *
 * The chips are `RemovablePill` on the `info-subtle` pairing — the exact
 * component and tone the pipeline Table row behind this modal already uses for
 * its tag chips (SE-6554), so removing a tag looks the same in the modal as it
 * does in the row that opened it. That replaces the previous hand-rolled pill,
 * which filled each chip with the raw tag colour on a fixed dark
 * `surface-action` slab and relied on a literal `×` glyph rather than the
 * pill's own remove affordance.
 *
 * The per-tag colour is deliberately no longer painted onto the chip: the
 * board renders tags in one tone everywhere else, and a tag-coloured fill
 * cannot guarantee contrast against arbitrary user-chosen colours. Flagged as
 * a product decision rather than made silently — see the handoff notes.
 * @param root0 - The component props
 * @param root0.currentPlayerTags - Array of tags currently assigned to the player
 * @param root0.onToggleTag - Callback to remove a tag when tapped
 */
function TagSelectorCurrentTagsContainer({
  currentPlayerTags,
  onToggleTag,
}: TagSelectorCurrentTagsProps) {
  const createTagToggleHandler = useCallback(
    (tag: Tag) => () => onToggleTag(tag),
    [onToggleTag]
  );

  if (currentPlayerTags.length === 0) {
    return null;
  }

  return (
    <Stack className="gap-2">
      <Text variant="caption" className="font-medium text-content-muted">
        CURRENT TAGS
      </Text>
      <Stack direction="horizontal" className="flex-wrap gap-2">
        {currentPlayerTags.map(tag => (
          <RemovablePill
            key={tag.id}
            label={tag.name}
            tone="info-subtle"
            onRemove={createTagToggleHandler(tag)}
            removeAccessibilityLabel={`Remove ${tag.name}`}
          />
        ))}
      </Stack>
    </Stack>
  );
}

export default TagSelectorCurrentTagsContainer;
